import { CommonModule } from '@angular/common';
import {
  ApplicationRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { notFutureDateValidator } from '../../../core/notFutureDate.validator';
import { atLeastOneConsentValidator } from 'app/core/consent.validator';
import { Address } from 'app/core/interfaces';
import { addressValidator } from 'app/core/address.validator';
import { dateOfBirthValidator } from 'app/core/dateBirth.validator';
import { bookingTypeScopedValidator } from '../../../core/bookingType.validator';
import { addressHasStateValidator } from 'app/core/state.validator';

type FormSubmitEvent =
  | {
      bookingType: 'newBooking';
      form: FormGroup;
      requestedLocation: Address;
      insuredAddress: Address;
    }
  | {
      bookingType: 'extension';
      form: FormGroup;
    };
@Component({
  selector: 'app-form-view',
  imports: [
    MatButtonModule,
    MatDatepickerModule,
    MatAccordion,
    MatExpansionModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCardModule,
    MatRadioModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatCheckboxModule,
    MatSelectModule,
    MatRadioModule,
    MatButtonModule,
    MatDatepickerModule,
    CommonModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './form-view.component.html',
  styleUrl: './form-view.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class FormViewComponent implements OnInit {
  constructor(private snackBar: MatSnackBar, private router: Router) {
    effect(() => {
      if (this.submitSuccess()) {
        this.router.navigate(['/case'], {
          state: { success: true },
        });
        this.form.reset();
      }

      if (this.submitError()) {
        this.snackBar.open(`❌ Error: ${this.submitError()}`, 'Close', {
          duration: 10000,
          panelClass: 'snackbar-center',
        });
      }
    });
  }

  today = new Date();

  private appRef = inject(ApplicationRef);
  ngOnInit(): void {
    this.createForm();

    this.nb('insuredAddress')?.valueChanges.subscribe((value) => {
      if (this.sameAs['requestedLocation']) {
        this.nb('requestedLocation')?.setValue(value, { emitEvent: false });
        this.requestedLocation = { ...this.insuredAddress };
      }
    });

    this.form.get('checkInDate')?.valueChanges.subscribe(() => {
      this.resetAccommodationIfNeeded();
    });

    this.form.get('checkOutDate')?.valueChanges.subscribe(() => {
      this.resetAccommodationIfNeeded();
    });
  }

  declare google: any;

  private fb = inject(FormBuilder);

  submitError = input<string | null>(null);
  submitSuccess = input<boolean>(false);

  isSubmittingInProgress = input<boolean>(false);

  formSubmit = output<FormSubmitEvent>();

  formReset = output<void>();

  //select fields
  accomodationRequirementList = input<string[]>();
  claimTypesList = input<string[]>();
  keyClientList = input<string[]>();

  form!: FormGroup;

  get f() {
    return this.form.controls;
  }

  private nb(path: string) {
    return this.form.get(`newBooking.${path}`);
  }

  createForm() {
    this.form = this.fb.group(
      {
        bookingType: ['newBooking', Validators.required],

        newBooking: this.fb.group({
          policyHolderFirstName: [
            '',
            [Validators.required, Validators.maxLength(80)],
          ],
          policyHolderSurname: [
            '',
            [Validators.required, Validators.maxLength(80)],
          ],
          policyHolderDateOfBirth: [
            null,
            [Validators.required, dateOfBirthValidator()],
          ],
          policyHolderPhone: [
            null,
            [Validators.required, Validators.minLength(7)],
          ],
          policyHolderAltPhone: [null, Validators.minLength(7)],
          policyHolderEmail: [null, [Validators.required, Validators.email]],

          nightlyBudget: [
            null,
            [Validators.required, Validators.pattern(/^\d+$/)],
          ],
          totalTenPercentLimit: [
            null,
            [Validators.required, Validators.pattern(/^\d+$/)],
          ],

          consentEmail: [false],
          consentSMS: [false],

          claimType: [null, Validators.required],
          incidentDate: [null, [Validators.required, notFutureDateValidator()]],
          incidentType: [null, Validators.required],

          insuredAddress: [
            this.insuredAddress.FormattedAddress || '',
            [
              Validators.required,
              addressHasStateValidator('insuredAddressData'),
            ],
          ],
          insuredAddressData: [this.insuredAddress || null],

          requestedLocation: [
            this.requestedLocation.FormattedAddress || '',
            [
              Validators.required,
              addressHasStateValidator('requestedLocationData'),
            ],
          ],
          requestedLocationData: [this.requestedLocation || null],

          checkInDate: [null, [Validators.required]],
          checkOutDate: [null, [Validators.required]],
          noCarParks: [null, [Validators.min(0)]],

          vehicleRegos: [null],
          mobilityIssues: [null],

          noAdults: [null, [Validators.required, Validators.min(1)]],
          noChildrenSmall: [null, [Validators.min(0)]],
          grownUpChildren: [null, [Validators.min(0)]],
          dogs: [null, [Validators.min(0)]],
          dogBreeds: [null],
          cats: [null, [Validators.min(0)]],
          catBreeds: [null],

          additionalNotes: [''],
          keyClient: [null, [Validators.required]],

          claimConsultantFirstName: [null, [Validators.required]],
          claimConsultantSurname: [null, [Validators.required]],
          claimConsultantExt: [null],
          claimConsultantEmail: ['home@autogeneral.com.au'],
          claimConsultantAdditionalNotes: [''],

          claimNumber: [null, [Validators.required]],
        }),

        extension: this.fb.group({
          caseNumber: [null, [Validators.required]],
          claimNumber: [null, [Validators.required]],
          checkInDate: [null, [Validators.required]],
          checkOutDate: [null, [Validators.required]],
          requestingConsultant: [null, [Validators.required]],
        }),
      },
      { validators: [bookingTypeScopedValidator()] }
    );

    this.form
      .get('bookingType')!
      .valueChanges.subscribe(() => this.toggleGroups());
    this.toggleGroups();
  }

  sameAsInsured(field: 'requestedLocation'): boolean {
    const insured = this.nb('insuredAddress')?.value;
    const requested = this.nb(field)?.value;
    return !!insured && !!requested && insured === requested;
  }

  toggleSameAsInsured(field: 'requestedLocation', checked: boolean): void {
    this.sameAs[field] = checked;

    const ctrl = this.nb(field);
    const insured = this.nb('insuredAddress')?.value;

    if (!ctrl) return;

    if (checked) {
      ctrl.setValue(insured);
      ctrl.disable({ emitEvent: false });

      this.requestedLocation = { ...this.insuredAddress };
    } else {
      ctrl.enable({ emitEvent: false });
    }

    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  onAddressChange(
    input: HTMLInputElement,
    field: 'insuredAddress' | 'requestedLocation'
  ): void {
    const ctrl = this.nb(field);
    if (!ctrl) return;

    const setData = (
      f: 'insuredAddress' | 'requestedLocation',
      mapped: Address | null
    ) => {
      const dataName =
        f === 'insuredAddress' ? 'insuredAddressData' : 'requestedLocationData';
      this.form
        .get(`newBooking.${dataName}`)
        ?.setValue(mapped, { emitEvent: false });
    };

    const syncRequestedIfNeeded = (
      formatted: string,
      mapped: Address | null
    ) => {
      if (field === 'insuredAddress' && this.sameAs['requestedLocation']) {
        this.nb('requestedLocation')?.setValue(formatted, { emitEvent: false });
        this.requestedLocation = mapped ? { ...mapped } : (null as any);

        setData('requestedLocation', mapped);

        this.nb('requestedLocation')?.updateValueAndValidity({
          emitEvent: false,
        });
      }
    };

    let ac = this.autocompleteMap.get(field);
    if (!ac) {
      ac = new google.maps.places.Autocomplete(input, {
        componentRestrictions: { country: 'AU' },
        types: ['address'],
      });

      this.autocompleteMap.set(field, ac);

      ac.addListener('place_changed', () => {
        this.selectingByAutocomplete[field] = true;

        const place = ac!.getPlace();
        const formatted = place?.formatted_address;

        if (formatted) {
          // string -> form control
          this.nb(field)?.setValue(formatted, { emitEvent: false });

          // object -> for API
          const mapped = this.mapPlaceToAddress(place);

          if (field === 'requestedLocation') {
            this.requestedLocation = mapped;
          } else {
            this.insuredAddress = mapped;
          }
          setData(field, mapped);

          syncRequestedIfNeeded(formatted, mapped);

          this.nb(field)?.markAsDirty();
          this.nb(field)?.updateValueAndValidity({ emitEvent: false });
          this.appRef.tick();
        }

        setTimeout(() => (this.selectingByAutocomplete[field] = false), 0);
      });
    }

    if (!this.selectingByAutocomplete[field]) {
      const value = input.value;
      this.nb(field)?.setValue(value, { emitEvent: false });

      setData(field, null);

      const manualObj: Address = {
        Landmark: '',
        Line: '',
        Town: '',
        Province: '',
        PostalCode: '',
        FormattedAddress: value,
        Lat: null,
        Lng: null,
      };

      if (field === 'requestedLocation') this.requestedLocation = manualObj;
      else this.insuredAddress = manualObj;

      syncRequestedIfNeeded(value, null);

      this.nb(field)?.updateValueAndValidity({ emitEvent: false });
    }
  }

  getDateDiff(): number {
    const checkIn = this.nb('checkInDate')?.value;
    const checkOut = this.nb('checkOutDate')?.value;
    if (!checkIn || !checkOut) return 0;

    const inDate = new Date(checkIn);
    const outDate = new Date(checkOut);
    const ms = outDate.getTime() - inDate.getTime();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  }

  private toggleGroups(): void {
    const type = this.form.get('bookingType')!.value;
    const newBooking = this.form.get('newBooking') as FormGroup;
    const extension = this.form.get('extension') as FormGroup;

    if (type === 'extension') {
      newBooking.disable({ emitEvent: false });
      extension.enable({ emitEvent: false });
    } else {
      extension.disable({ emitEvent: false });
      newBooking.enable({ emitEvent: false });
    }

    this.form.updateValueAndValidity({ emitEvent: false });
  }

  requestedLocation: Address = {
    Landmark: '',
    Line: '',
    Town: '',
    Province: '',
    PostalCode: '',
    FormattedAddress: '',
    Lat: null,
    Lng: null,
  };

  insuredAddress: Address = {
    Landmark: '',
    Line: '',
    Town: '',
    Province: '',
    PostalCode: '',
    FormattedAddress: '',
    Lat: null,
    Lng: null,
  };

  submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const bookingType = this.form.get('bookingType')?.value as
      | 'newBooking'
      | 'extension';

    const selectedForm = this.form.get(bookingType) as FormGroup;

    if (bookingType === 'extension') {
      this.formSubmit.emit({ bookingType, form: selectedForm });
      return;
    }
    this.formSubmit.emit({
      bookingType,
      form: selectedForm,
      requestedLocation: this.requestedLocation,
      insuredAddress: this.insuredAddress,
    });
  }
  reset(): void {
    const currentType = this.form.get('bookingType')?.value ?? 'newBooking';

    this.form.reset({
      bookingType: currentType,

      newBooking: {
        keyClient: null,
        claimConsultantEmail: 'home@autogeneral.com.au',
        consentEmail: false,
        consentSMS: false,
      },

      extension: {
        caseNumber: null,
        claimNumber: null,
        extensionFrom: null,
        extensionTo: null,
        requestingConsultant: null,
      },
    });

    if (currentType === 'extension') {
      this.form.get('newBooking')?.disable({ emitEvent: false });
      this.form.get('extension')?.enable({ emitEvent: false });
    } else {
      this.form.get('extension')?.disable({ emitEvent: false });
      this.form.get('newBooking')?.enable({ emitEvent: false });
    }

    this.form.markAsPristine();
    this.form.markAsUntouched();

    this.form.updateValueAndValidity({ emitEvent: false });
  }

  sameAs: Record<string, boolean> = {
    insuredAddress: false,
    requestedLocation: false,
  };

  private autocompleteMap = new Map<string, google.maps.places.Autocomplete>();
  private selectingByAutocomplete: Record<string, boolean> = {};

  private mapPlaceToAddress(place: google.maps.places.PlaceResult) {
    const components: any = {};

    place.address_components?.forEach((c) => {
      if (c.types.includes('route')) components.Line = c.long_name;
      if (c.types.includes('locality')) components.Town = c.long_name;

      if (c.types.includes('postal_code')) components.PostalCode = c.long_name;
      if (c.types.includes('administrative_area_level_1'))
        components.Province = c.short_name || c.long_name;
      if (c.types.includes('point_of_interest'))
        components.Landmark = c.long_name;
      if (c.types.includes('establishment') && !components.Landmark)
        components.Landmark = c.long_name;
    });

    return {
      Landmark: components.Landmark || '',
      Line: components.Line || '',
      Town: components.Town || '',
      Province: components.Province,
      PostalCode: components.PostalCode || '',
      FormattedAddress: place.formatted_address || '',
      Lat: place.geometry?.location?.lat() || null,
      Lng: place.geometry?.location?.lng() || null,
    };
  }

  //check terms by date
  private resetAccommodationIfNeeded() {
    const diff = this.getDateDiff();
    const currentValue = this.form.get('incidentType')?.value;

    if (
      (diff > 7 && currentValue === 'Short Term') ||
      (diff <= 7 && currentValue === 'Long Term')
    ) {
      this.form.get('incidentType')?.reset();
    }
  }
}
